import { ContentItemViewNodesType } from '../types'
import { syncVisibilityBetweenBODAndManufacturers } from './bodUtils'

/**
 * Retrieves parent article node of a given content node.
 *
 */
export const getParentArticleNode = (
    currentNode: Partial<ContentItemViewNodesType>,
    contentNodes: Partial<ContentItemViewNodesType>[]
): Partial<ContentItemViewNodesType> => {
    if (currentNode?.textType === 'Article') {
        return currentNode
    } else {
        const parentNode = contentNodes?.find((item) =>
            currentNode?.dtvContentItemParentId
                ? item?.id === currentNode?.dtvContentItemParentId
                : item?.dtvItemId === currentNode?.dtvParentId
        )
        if (parentNode) {
            return getParentArticleNode(parentNode, contentNodes)
        } else return {}
    }
}

/**
 * Determines if dtvParentId should be skipped when finding children.
 * Skip dtvParentId for nested paragraphs to prevent
 * incorrect sibling paragraphs from being treated as children.
 *
 * @param node - The node to check
 * @returns True if dtvParentId should be skipped, false otherwise
 */
const shouldSkipDtvParentId = (
    node: Partial<ContentItemViewNodesType>
): boolean => {
    // Only skip for paragraphs that have an explicit parent (nested paragraphs)
    // Top-level paragraphs (dtvContentItemParentId is null/undefined/empty) should use dtvParentId
    return (
        node?.textType === 'Paragraph' &&
        node?.dtvContentItemParentId != null &&
        node?.dtvContentItemParentId !== ''
    )
}

/**
 * Retrieves child nodes of a given content node based on the hierarchical level.
 *
 * @param level - The level in the hierarchy (0 for direct children, 1 or more for deeper levels).
 * @param hoveredNode - The node currently being hovered over; used as a reference for matching children.
 * @param contentNodes - The complete list of content nodes to filter from.
 * @param parentNodes - (Optional) The list of parent nodes to match children against when level > 0.
 * @returns A filtered list of nodes that are children of the hovered or parent nodes.
 */
const getChildContentItemViewNodes = (
    level: number,
    hoveredNode: Partial<ContentItemViewNodesType>,
    contentNodes: Partial<ContentItemViewNodesType>[],
    parentNodes?: Partial<ContentItemViewNodesType>[]
) => {
    if (level === 0) {
        // Match TreeDataMapper.findChildren() logic for consistency
        const children = contentNodes?.filter((item) => {
            // Check for BOD child relationship
            const isBodChildNode = item?.refBodItemId === hoveredNode?.dtvItemId

            // Check for content item parent relationship
            const isChildNode =
                (!item?.refBodItemId || item?.refBodItemId === '') &&
                item?.dtvContentItemParentId === hoveredNode?.id

            // Check for direct DTV parent relationship
            const isDirectChildNode =
                item?.dtvParentId === hoveredNode?.dtvItemId &&
                !item?.dtvContentItemParentId &&
                (!item?.refBodItemId || item?.refBodItemId === '') &&
                !shouldSkipDtvParentId(hoveredNode)

            return isChildNode || isDirectChildNode || isBodChildNode
        })

        return children
    } else {
        // For levels > 0, find children of the parent nodes from the previous level
        return contentNodes?.filter((item) => {
            // Check if this item is a child of any parent node from the previous level
            return parentNodes?.some((parentNode) => {
                // Check for BOD child relationship
                const isBodChildNode =
                    item?.refBodItemId === parentNode?.dtvItemId

                // Check for content item parent relationship
                const isChildNode =
                    (!item?.refBodItemId || item?.refBodItemId === '') &&
                    item?.dtvContentItemParentId === parentNode?.id

                // Check for direct DTV parent relationship
                const isDirectChildNode =
                    item?.dtvParentId === parentNode?.dtvItemId &&
                    !item?.dtvContentItemParentId &&
                    (!item?.refBodItemId || item?.refBodItemId === '') &&
                    !shouldSkipDtvParentId(parentNode)

                return isChildNode || isDirectChildNode || isBodChildNode
            })
        })
    }
}

/**
 * Retrieves all child nodes of a given content node.
 *
 */
export const getChildElements = (
    hoveredNode: Partial<ContentItemViewNodesType>,
    contentNodes: Partial<ContentItemViewNodesType>[]
) => {
    const levelOneChild = getChildContentItemViewNodes(
        0,
        hoveredNode,
        contentNodes
    )
    const levelTwoChild = getChildContentItemViewNodes(
        1,
        hoveredNode,
        contentNodes,
        levelOneChild
    )
    const levelThreeChild = getChildContentItemViewNodes(
        2,
        hoveredNode,
        contentNodes,
        levelTwoChild
    )
    // Add level 4 for deeply nested children
    const levelFourChild = getChildContentItemViewNodes(
        3,
        hoveredNode,
        contentNodes,
        levelThreeChild
    )
    // Add level 5 for even deeper nesting
    const levelFiveChild = getChildContentItemViewNodes(
        4,
        hoveredNode,
        contentNodes,
        levelFourChild
    )

    return [
        ...levelOneChild,
        ...levelTwoChild,
        ...levelThreeChild,
        ...levelFourChild,
        ...levelFiveChild,
    ]
}
/**
 * Retrieves all parent nodes upto top level
 *
 */

export const findAncestors = (
    contentNodes: Partial<ContentItemViewNodesType>[],
    currentNode: Partial<ContentItemViewNodesType>
) => {
    const contentItemIdMap = new Map<
        string,
        Partial<ContentItemViewNodesType>
    >()
    const dtvItemIdMap = new Map<string, Partial<ContentItemViewNodesType>>()

    contentNodes.forEach((node) => {
        if (node.id) {
            contentItemIdMap.set(node.id, node)
        }
        if (node.dtvItemId) {
            dtvItemIdMap.set(node.dtvItemId, node)
        }
    })

    let ancestors = new Array()
    let localNode = currentNode

    while (localNode) {
        const parentViaContentItemId = localNode.dtvContentItemParentId
            ? contentItemIdMap.get(localNode.dtvContentItemParentId)
            : undefined
        const parentViaDtvItemId = localNode.dtvParentId
            ? dtvItemIdMap.get(localNode.dtvParentId)
            : undefined

        const parent = parentViaContentItemId || parentViaDtvItemId

        if (!parent) break
        if (ancestors) {
            ancestors?.push(parent)
        }
        localNode = parent
    }

    return ancestors
}

/**
 * check if all content element nodes inside parent article node are hidden
 *
 */
export const areAllChildrenInsideArticleHidden = (
    hoveredNode: Partial<ContentItemViewNodesType>,
    contentNodes: Partial<ContentItemViewNodesType>[],
    isHidden: boolean
) => {
    if (!isHidden) return false
    const articleNode = getParentArticleNode(hoveredNode, contentNodes)
    const childNodes = getChildElements(articleNode, contentNodes)
    const activeElementNodes = childNodes?.filter(
        (item) => !item?.isHidden && item?.id !== hoveredNode?.id
    )

    return activeElementNodes?.length === 0
}

/**
 * Determines if a node's explicit state should be preserved during visibility changes.
 */
const shouldPreserveNodeExplicitState = (
    item: Partial<ContentItemViewNodesType>,
    hoveredNode: Partial<ContentItemViewNodesType>,
    nodeIdsToUpdate: Set<string>,
    isParentAncestor: boolean,
    isHidden: boolean
): boolean => {
    const hasOppositeExplicitState = (item.isHidden ?? false) === !isHidden

    // Don't preserve if it doesn't have an opposite state
    if (!hasOppositeExplicitState) {
        return false
    }

    // Never preserve parent ancestors when activating
    if (isParentAncestor) {
        return false
    }

    // Check if this item is in the nodeIdsToUpdate set (is a child of the hovered node)
    const isChildOfHoveredNode = item?.id ? nodeIdsToUpdate.has(item.id) : false

    // Don't preserve children of the hovered node - they should be updated with their parent
    if (isChildOfHoveredNode) {
        return false
    }

    // Check if this is a mislinked node (has same parent but linked as child)
    const isMislinkedNode =
        item?.dtvContentItemParentId === hoveredNode?.id &&
        item?.dtvParentId === hoveredNode?.dtvParentId

    // Only preserve:
    // - Mislinked nodes (incorrectly linked siblings)
    // - True siblings with explicit opposite state (not children of hovered node)
    // - Unrelated nodes with explicit opposite state
    return isMislinkedNode || !isChildOfHoveredNode
}

/**
 * Finds all related child nodes up to three levels deep from the hovered node
 * and updates their visibility based on the `isHidden` flag.
 *
 * Note: This function updates all child nodes of the hovered node. If nodes appear
 * as siblings in the UI but have a parent-child relationship via dtvContentItemParentId,
 * they will all be updated together. This is the expected behavior for maintaining
 * consistency in the tree structure.
 *
 * @param hoveredNode - The content node that is the current point of reference.
 * @param contentNodes - The complete list of content nodes.
 * @param isHidden - Flag indicating whether the matched nodes should be hidden or shown.
 * @returns A new array of content nodes with `isHidden` updated for the matched nodes.
 */

export const getAllChildrenFromHoveredNode = (
    hoveredNode: Partial<ContentItemViewNodesType>,
    contentNodes: Partial<ContentItemViewNodesType>[],
    isHidden: boolean
) => {
    //check for manufacturer/BOD paragraph
    if (hoveredNode?.isBOD || hoveredNode?.isManufacturers) {
        return syncVisibilityBetweenBODAndManufacturers(
            hoveredNode,
            contentNodes,
            isHidden
        )
    }
    const nodesToUpdate = getChildElements(hoveredNode, contentNodes)
    const articleNode = getParentArticleNode(hoveredNode, contentNodes)
    const parentAncestorNodes = isHidden
        ? []
        : findAncestors(contentNodes, hoveredNode)

    // Pre-compute node IDs for efficient lookup (performance optimization)
    const nodeIdsToUpdate = new Set<string>(
        nodesToUpdate
            .map((node) => node?.id)
            .filter((id): id is string => id !== undefined && id !== null)
    )

    let updatedNodes = contentNodes.map((item) => {
        const shouldUpdate =
            parentAncestorNodes?.some(
                (ancestor) => ancestor?.id === item?.id
            ) ||
            (item?.id && nodeIdsToUpdate.has(item.id)) ||
            item?.id === hoveredNode?.id ||
            (!isHidden && item?.id === articleNode?.id) ||
            (!isHidden &&
                (item?.dtvItemId === hoveredNode?.dtvParentId ||
                    item?.id === hoveredNode.dtvContentItemParentId))

        if (shouldUpdate) {
            // Always update the hovered node itself
            if (item?.id === hoveredNode?.id) {
                // When un-hiding deactivated content, also clear the isDeactivated flag
                const updates = { isHidden }
                if (!isHidden && item?.isDeactivated) {
                    return { ...item, ...updates, isDeactivated: false }
                }
                return { ...item, ...updates }
            }

            // When activating, parent ancestors must be activated
            const isParentAncestor =
                !isHidden &&
                parentAncestorNodes?.some(
                    (ancestor) => ancestor?.id === item?.id
                )

            const shouldPreserveExplicitState = shouldPreserveNodeExplicitState(
                item,
                hoveredNode,
                nodeIdsToUpdate,
                isParentAncestor,
                isHidden
            )

            if (shouldPreserveExplicitState) {
                return item
            }
            // When un-hiding deactivated content, also clear the isDeactivated flag
            const updates = { isHidden }
            if (!isHidden && item?.isDeactivated) {
                return { ...item, ...updates, isDeactivated: false }
            }
            return { ...item, ...updates }
        }

        return item
    })
    // ensure BOD/Manufacturer selection options also reflect visibility state
    const specialNodes = nodesToUpdate.filter(
        (node) => node?.isBOD || node?.isManufacturers
    )
    for (const bodNode of specialNodes) {
        updatedNodes = syncVisibilityBetweenBODAndManufacturers(
            bodNode,
            updatedNodes,
            isHidden
        )
    }
    // hide article if all its children are hidden
    const shouldHideArticle =
        isHidden &&
        areAllChildrenInsideArticleHidden(hoveredNode, updatedNodes, isHidden)
    if (shouldHideArticle && articleNode?.id !== hoveredNode?.id) {
        updatedNodes = updatedNodes.map((item) =>
            item?.id === articleNode?.id ? { ...item, isHidden: true } : item
        )
    }
    return updatedNodes
}
